import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { authClient } from '../auth-client.ts';

export function LoginPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'password' | 'totp' | 'backup'>('password');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    const next = params.get('next');
    navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/', {
      replace: true,
    });
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await authClient.signIn.email({ email: email.trim(), password });
    setBusy(false);
    if (r.error) {
      setError(r.error.status === 429 ? t('errors.rate_limited') : t('login.failed'));
      return;
    }
    if (r.data && 'twoFactorRedirect' in r.data && r.data.twoFactorRedirect) {
      setStep('totp');
      return;
    }
    await done();
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r =
      step === 'totp'
        ? await authClient.twoFactor.verifyTotp({ code: code.trim() })
        : await authClient.twoFactor.verifyBackupCode({ code: code.trim() });
    setBusy(false);
    if (r.error) {
      setError(r.error.status === 429 ? t('errors.rate_limited') : t('login.badCode'));
      return;
    }
    await done();
  };

  return (
    <main className="narrow" id="main">
      <div className="card">
        <h2>{t('login.title')}</h2>
        {step === 'password' ? (
          <form onSubmit={submitPassword}>
            <label>
              {t('login.email')}
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              {t('login.password')}
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && (
              <p className="err" role="alert">
                {error}
              </p>
            )}
            <div className="bar">
              <button className="btn" disabled={busy}>
                {t('login.submit')}
              </button>
              <span className="sp" />
              <Link to="/forgot-password">{t('login.forgot')}</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <p className="k">{t('login.codeHint')}</p>
            <label>
              {step === 'totp' ? t('login.code') : t('login.backupCode')}
              <input
                inputMode={step === 'totp' ? 'numeric' : 'text'}
                autoComplete="one-time-code"
                pattern={step === 'totp' ? '[0-9]{6}' : undefined}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            {error && (
              <p className="err" role="alert">
                {error}
              </p>
            )}
            <div className="bar">
              <button className="btn" disabled={busy}>
                {t('login.verify')}
              </button>
              <span className="sp" />
              {step === 'totp' && (
                <button type="button" className="btn link" onClick={() => setStep('backup')}>
                  {t('login.useBackup')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
